import React, { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/auth";
import { toast } from "sonner";
import { PageHeader } from "@/pages/Classes";
import { Send, Sparkles } from "lucide-react";

export default function Hire() {
  const [options, setOptions] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [form, setForm] = useState({ full_name: "", email: "", phone: "", event_date: "", event_location: "", message: "" });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/hire-options").then(r => {
      const list = r.data || [];
      setOptions(list);
      // Functional setter avoids reading selectedId from closure (no missing-dep warning)
      setSelectedId(prev => prev || (list[0] && list[0].id) || "");
    }).catch(() => setOptions([]));
  }, []);

  const selected = useMemo(() => options.find(o => o.id === selectedId), [options, selectedId]);

  const submit = async () => {
    if (!selectedId) { toast.error("Pick a hire option"); return; }
    if (!form.full_name.trim() || !form.email.trim()) { toast.error("Name and email are required"); return; }
    setSaving(true);
    try {
      await api.post("/hire-inquiries", {
        ...form,
        hire_option_id: selectedId,
        hire_option_name: selected?.name,
      });
      toast.success("Your inquiry has been submitted.");
      setForm({ full_name: "", email: "", phone: "", event_date: "", event_location: "", message: "" });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to send");
    } finally { setSaving(false); }
  };

  return (
    <>
      <PageHeader eyebrow="Book us for your event" title="Hire Us" sub="Choreographers · Performing crew · Custom concepts. Pick an option, drop your event brief and we'll come back with a quote." />
      <div className="max-w-[1100px] mx-auto px-4 sm:px-6 py-12 grid lg:grid-cols-5 gap-8" data-testid="hire-page">

        {/* LEFT — option picker + description */}
        <div className="lg:col-span-3 space-y-6">
          <div>
            <label className="eyebrow block mb-2">What do you want to hire?</label>
            <select
              value={selectedId}
              onChange={e => setSelectedId(e.target.value)}
              className="w-full bg-black border border-white/10 px-4 py-3 text-base focus:border-red-600 outline-none"
              data-testid="hire-option-select"
            >
              {options.length === 0 && <option value="">Loading options…</option>}
              {options.map(o => (
                <option key={o.id} value={o.id}>{o.name}</option>
              ))}
            </select>
          </div>

          <div className="card-surface p-6" data-testid="hire-option-description">
            {selected ? (
              <>
                <div className="eyebrow flex items-center gap-2 mb-3"><Sparkles size={12}/>About this option</div>
                <h3 className="font-display text-2xl uppercase mb-3">{selected.name}</h3>
                <p className="text-zinc-300 leading-relaxed whitespace-pre-wrap">{selected.description || "No description yet — send us a message and we'll get in touch."}</p>
              </>
            ) : (
              <div className="text-zinc-500">Pick an option to see the details.</div>
            )}
          </div>

          <div className="text-zinc-500 text-xs">
            Can't see what you need? Pick the closest option and describe your brief in the message field — we'll tailor a quote.
          </div>
        </div>

        {/* RIGHT — inquiry form */}
        <div className="lg:col-span-2">
          <div className="card-surface p-6 space-y-3">
            <div className="eyebrow flex items-center gap-2 mb-2"><Send size={12}/>Send inquiry</div>
            <input placeholder="Full name *" value={form.full_name} onChange={e => setForm({...form, full_name: e.target.value})} className="w-full bg-black border border-white/10 px-3 py-2 text-sm focus:border-red-600 outline-none" data-testid="hire-name"/>
            <input placeholder="Email *" type="email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="w-full bg-black border border-white/10 px-3 py-2 text-sm focus:border-red-600 outline-none" data-testid="hire-email"/>
            <input placeholder="Phone" value={form.phone} onChange={e => setForm({...form, phone: e.target.value})} className="w-full bg-black border border-white/10 px-3 py-2 text-sm focus:border-red-600 outline-none" data-testid="hire-phone"/>
            <div className="grid grid-cols-2 gap-2">
              <div>
                <label className="text-[10px] uppercase tracking-widest text-zinc-500 block mb-1">Event date</label>
                <input type="date" value={form.event_date} onChange={e => setForm({...form, event_date: e.target.value})} className="w-full bg-black border border-white/10 px-3 py-2 text-sm focus:border-red-600 outline-none" data-testid="hire-event-date"/>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-zinc-500 block mb-1">Location</label>
                <input placeholder="City / venue" value={form.event_location} onChange={e => setForm({...form, event_location: e.target.value})} className="w-full bg-black border border-white/10 px-3 py-2 text-sm focus:border-red-600 outline-none" data-testid="hire-event-location"/>
              </div>
            </div>
            <textarea rows={5} placeholder="Tell us about your event — audience, duration, style, budget range…" value={form.message} onChange={e => setForm({...form, message: e.target.value})} className="w-full bg-black border border-white/10 px-3 py-2 text-sm focus:border-red-600 outline-none" data-testid="hire-message"/>
            <button
              onClick={submit}
              disabled={saving || !selectedId || !form.full_name.trim() || !form.email.trim()}
              className="btn-red w-full disabled:opacity-40"
              data-testid="hire-submit"
            >
              {saving ? "Sending…" : "Send inquiry"}
            </button>
            <p className="text-zinc-500 text-[11px]">For urgent requests call the studio.</p>
          </div>
        </div>
      </div>
    </>
  );
}
